import { flushSync } from 'react-dom';
import { printElement } from '../../../printing/printElement';
import { FormulaText } from '../../../components/FormulaText';
import { moleculeThumbnailSvg } from '../../../components/MoleculeThumbnail';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommentAnchor } from '@peruselab/api-client/discussions';
import { MoleculeVisibility } from './MoleculeVisibility';
import { ScientificMeta } from '../../public/CatalogueMeta';
import { getMoleculeSummary, type MoleculeSummary, type PublishedStatement } from '@peruselab/api-client/molecules';
import { DiscussionThread } from '../../discussions/DiscussionThread';
import { MonographCards } from '../../monograph/MonographCards';
import { MonographHeaderBar } from '../../monograph/MonographHeaderBar';
import { MonographSheet } from '../../monograph/MonographSheet';
import { useMonographView } from '../../monograph/MonographViewSwitch';
import { StatementDiscussion } from './FactsSection';
import { PropertiesSection } from './PropertiesSection';
import { useMoleculeFacts } from './PublishedFacts';
import { useMoleculeProperties } from './useMoleculeProperties';

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly summary: MoleculeSummary }
  | { readonly status: 'notFound' };

/**
 * ADR 0017 (#904, #906): a Molecule's factsheet -- its picture, drawn by our own renderer from an
 * automatic layout (D4); identifiers we compute plus PubChem's when it has a record (D5); PubChem's
 * sourced statements (About, Uses, Learn more; D1–D3); and its discussion. A structure no database knows still gets the whole page, and says so (principle 4).
 * The same body serves the object page and the public address (D10).
 */
export function MoleculeFactsheet({
  moleculeId,
  catalogue = false
}: {
  readonly moleculeId: string;
  /** ADR 0023 (#1113): the public catalogue page -- it tells search engines what it is. */
  readonly catalogue?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<State>({ status: 'loading' });
  const facts = useMoleculeFacts(moleculeId);
  // #929: the Monograph opens in the reader's view (Cards by default, #928); both share these.
  const view = useMonographView();
  const [printBusy, setPrintBusy] = useState(false);
  const [printError, setPrintError] = useState(false);
  const [printSheet, setPrintSheet] = useState(false);
  const properties = useMoleculeProperties(moleculeId, facts?.fetchedAt ?? null);
  // #910 (ADR 0017 D11): "Discuss" on a statement starts a comment anchored to it; tapping such a
  // comment's anchor (or an address ending in #s-…) brings the statement back into view.
  const [discussing, setDiscussing] = useState<CommentAnchor | null>(null);
  const [writeRequest, setWriteRequest] = useState(0);
  const [focusKey, setFocusKey] = useState<string | null>(() =>
    globalThis.location?.hash?.startsWith('#s-') ? globalThis.location.hash.slice(1) : null
  );
  const onDiscuss = useCallback((statement: PublishedStatement, key: string) => {
    const quote = statement.text.length > 300 ? `${statement.text.slice(0, 299)}…` : statement.text;
    // #947 (S7): a statement that's an assertion is discussed like any value -- an assertion anchor.
    if (statement.assertionId) {
      setDiscussing({ kind: 'assertion', assertionId: statement.assertionId, label: quote.slice(0, 200) });
      setWriteRequest((request) => request + 1);
      return;
    }
    setDiscussing({ kind: 'statement', statementKey: key, quote, source: statement.source.slice(0, 200) });
    setWriteRequest((request) => request + 1);
  }, []);
  // #920 (D16): the same for a property value.
  const [focusAssertionId, setFocusAssertionId] = useState<string | null>(() =>
    globalThis.location?.hash?.startsWith('#a-') ? globalThis.location.hash.slice(3) : null
  );
  const onDiscussValue = useCallback((assertion: { readonly id: string }, label: string) => {
    setDiscussing({ kind: 'assertion', assertionId: assertion.id, label: label.slice(0, 200) });
    setWriteRequest((request) => request + 1);
  }, []);
  const showAnchor = useCallback((anchor: CommentAnchor) => {
    if (anchor.kind === 'assertion') {
      setFocusAssertionId(null);
      globalThis.setTimeout(() => setFocusAssertionId(anchor.assertionId), 0);
      return;
    }
    if (anchor.kind !== 'statement') return;
    // Re-set even when unchanged, so tapping again scrolls again.
    setFocusKey(null);
    globalThis.setTimeout(() => setFocusKey(anchor.statementKey), 0);
  }, []);
  // An address naming a statement (#s-…), on load or later (the workspace opens one in a new tab).
  useEffect(() => {
    const onHashChange = () => {
      const hash = globalThis.location.hash;
      if (hash.startsWith('#s-')) setFocusKey(hash.slice(1));
      if (hash.startsWith('#a-')) setFocusAssertionId(hash.slice(3));
    };
    globalThis.addEventListener('hashchange', onHashChange);
    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, []);
  const statementDiscussion = useMemo(
    () => ({ onDiscuss, focusKey, focusAssertionId }),
    [onDiscuss, focusKey, focusAssertionId]
  );

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getMoleculeSummary(moleculeId)
      .then((summary) => {
        if (!cancelled) setState({ status: 'ready', summary });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'notFound' });
      });
    return () => {
      cancelled = true;
    };
  }, [moleculeId]);

  const summary = state.status === 'ready' ? state.summary : null;
  // #1079: PubChem is first asked when this page opens, so its record can turn up in the facts
  // after the header loaded. Then the header (name, PubChem link) and the registration line
  // (public through PubChem's record, the credit's outside-record note) catch up -- once.
  const refreshedForCid = useRef<number | null>(null);
  useEffect(() => {
    const cid = facts?.pubchemCid ?? null;
    if (cid === null || !summary || summary.pubchemCid || refreshedForCid.current === cid) return;
    refreshedForCid.current = cid;
    let cancelled = false;
    getMoleculeSummary(moleculeId)
      .then((next) => {
        if (!cancelled) setState({ status: 'ready', summary: next });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [facts?.pubchemCid, summary, moleculeId]);
  // #906: PubChem's display title ("Aspirin") when it has one, ahead of the IUPAC-style name.
  const name = facts?.title ?? summary?.name ?? null;
  const title = name ?? summary?.molecularFormula ?? null;

  useEffect(() => {
    // The catalogue page's title is ScientificMeta's (ADR 0023 D3).
    if (!title || catalogue) return;
    const previous = document.title;
    document.title = t('moleculePage.documentTitle', { name: title });
    return () => {
      document.title = previous;
    };
  }, [title, t, catalogue]);

  if (state.status === 'loading') {
    return (
      <p className="molecule-factsheet-status" role="status">
        {t('common.loading')}
      </p>
    );
  }
  if (!summary) {
    return (
      <div className="molecule-factsheet-status">
        <h1>{t('moleculePage.notFoundTitle')}</h1>
        <p>{t('moleculePage.notFound')}</p>
      </div>
    );
  }

  const weight =
    summary.molecularWeight === null
      ? null
      : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(summary.molecularWeight);
  // #1079: an unusual charge wasn't looked up at all -- UnusualChargeNote says so instead.
  const noPublished = facts?.status !== 'implausible' && (!summary.pubchemCid || facts?.status === 'none');

  const discussion = (
    <DiscussionThread
      kind="molecule"
      artifactId={summary.id}
      selectionAnchor={discussing}
      writeRequest={writeRequest}
      onShowAnchor={showAnchor}
    />
  );

  const renderSheet = (printing = false) => (
    <MonographSheet
      summary={summary}
      facts={facts}
      name={name}
      formula={summary.molecularFormula ? <FormulaText formula={summary.molecularFormula} /> : null}
      weight={weight}
      noPublished={noPublished}
      properties={properties}
      renderPropertiesTable={({ addRequest }) => (
        <PropertiesSection
          moleculeId={summary.id}
          properties={properties}
          onDiscuss={onDiscussValue}
          focusAssertionId={focusAssertionId}
          addRequest={addRequest}
          layout="table"
        />
      )}
      discussion={printing ? null : discussion}
    />
  );
  const printProfile = async () => {
    if (printBusy) return;
    setPrintBusy(true);
    setPrintError(false);
    try {
      const svg = await moleculeThumbnailSvg(summary.id);
      flushSync(() => setPrintSheet(true));
      const content = document.getElementById('molecule-print-sheet')?.cloneNode(true) as HTMLElement | undefined;
      if (!content) throw new Error('Profile sheet unavailable');
      const picture = content.querySelector('.monograph-hero-picture');
      // Trusted renderer output; avoid racing the thumbnail component's asynchronous mount.
      if (picture) picture.innerHTML = svg;
      flushSync(() => setPrintSheet(false));
      await printElement(content, title ?? t('printing.profile'));
    } catch {
      setPrintError(true);
    } finally {
      setPrintSheet(false);
      setPrintBusy(false);
    }
  };
  const printContent = (
    <>
      {printError ? <p role="alert">{t('printing.error')}</p> : null}
      {printSheet ? (
        <div id="molecule-print-sheet" hidden>
          {renderSheet(true)}
        </div>
      ) : null}
    </>
  );

  if (view === 'cards') {
    return (
      <StatementDiscussion.Provider value={statementDiscussion}>
        <div className="monograph">
          <MonographHeaderBar name={title} onPrint={() => void printProfile()} printBusy={printBusy} />
          <MoleculeVisibility
            key={`${summary.id}:${summary.pubchemCid ?? ''}`}
            moleculeId={summary.id}
            name={title ?? t('moleculePage.untitled')}
          />
          {printContent}
          <MonographCards
            summary={summary}
            facts={facts}
            name={name}
            formula={summary.molecularFormula ? <FormulaText formula={summary.molecularFormula} /> : null}
            weight={weight}
            noPublished={noPublished}
            properties={properties}
            focusAssertionId={focusAssertionId}
            focusStatementKey={focusKey}
            renderPropertiesList={({ focusPropertyId, addRequest, propertyId, displayUnit, onAdded }) => (
              <PropertiesSection
                moleculeId={summary.id}
                properties={properties}
                onDiscuss={onDiscussValue}
                focusAssertionId={focusAssertionId}
                focusPropertyId={focusPropertyId}
                addRequest={addRequest}
                layout="evidence"
                propertyId={propertyId}
                displayUnit={displayUnit}
                onAdded={onAdded}
              />
            )}
            discussion={discussion}
          />
        </div>
      </StatementDiscussion.Provider>
    );
  }

  // #930: the reference sheet.
  return (
    <StatementDiscussion.Provider value={statementDiscussion}>
      {catalogue ? <ScientificMeta summary={summary} facts={facts} /> : null}
      <div className="monograph">
        <MonographHeaderBar name={title} onPrint={() => void printProfile()} printBusy={printBusy} />
        <MoleculeVisibility
          key={`${summary.id}:${summary.pubchemCid ?? ''}`}
          moleculeId={summary.id}
          name={title ?? t('moleculePage.untitled')}
        />
        {printContent}
        {renderSheet()}
      </div>
    </StatementDiscussion.Provider>
  );
}
